import { useEffect, useRef } from "react";
import { motion, useInView, animate } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal, EASE } from "./Reveal";

const CountUp = ({ value, plus = false, raw = false }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.8,
      ease: EASE,
      onUpdate: (v) => {
        if (ref.current) ref.current.textContent = (raw ? Math.round(v).toString() : Math.round(v).toLocaleString("en-US")) + (plus ? "+" : "");
      },
    });
    return () => controls.stop();
  }, [inView, value, plus, raw]);
  return <span ref={ref}>{raw ? value : `0${plus ? "+" : ""}`}</span>;
};

const Node = ({ title, desc, delay }) => (
  <Reveal delay={delay} className="card-surface p-6 h-full relative overflow-hidden">
    <div className="absolute top-0 left-0 w-10 h-px bg-hadron-cyan" />
    <h3 className="font-display uppercase font-bold text-2xl text-txt">{title}</h3>
    <p className="mt-3 text-sm text-txt-2 leading-relaxed">{desc}</p>
  </Reveal>
);

export const Synergy = () => {
  const { t } = useLang();
  const s = t.synergy;
  const stats = [
    { v: 5000, plus: true, l: s.s1l },
    { v: 4000, plus: true, l: s.s2l, suffix: " m²" },
    { v: 550, plus: false, l: s.s3l, suffix: " A" },
    { v: 2014, plus: false, l: s.s4l, raw: true },
  ];

  return (
    <section id="synergy" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <p className="mono-label">{s.eyebrow}</p>
          <h2 className="mt-4 font-display uppercase font-bold tracking-tight leading-[0.95] text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
            {s.heading}
          </h2>
          <p className="mt-6 max-w-2xl text-txt-2 leading-relaxed">{s.lead}</p>
          {s.brandNote && (
            <p className="mt-4 font-mono2 text-[11px] uppercase tracking-[0.18em] text-hadron-cyan/70" data-testid="brand-note">{s.brandNote}</p>
          )}
        </Reveal>

        {/* three nodes */}
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          <Node title={s.n1t} desc={s.n1d} delay={0} />
          <Node title={s.n2t} desc={s.n2d} delay={0.12} />
          <Node title={s.n3t} desc={s.n3d} delay={0.24} />
        </div>

        {/* mission & vision */}
        <div className="mt-5 grid md:grid-cols-2 gap-5">
          <Reveal className="card-surface p-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-10 h-px bg-hadron-green" />
            <p className="mono-label !text-[9px]">{s.missionLabel}</p>
            <p className="mt-4 text-lg text-txt leading-relaxed" data-testid="mission-quote">“{s.mission}”</p>
          </Reveal>
          <Reveal delay={0.1} className="card-surface p-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-10 h-px bg-hadron-cyan" />
            <p className="mono-label !text-[9px]">{s.visionLabel}</p>
            <p className="mt-4 text-lg text-txt leading-relaxed" data-testid="vision-quote">“{s.vision}”</p>
          </Reveal>
        </div>

        {/* stats band */}
        <Reveal delay={0.1} className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line" data-testid="synergy-stats">
          {stats.map((st, i) => (
            <div key={i} className="bg-ink p-6 lg:p-8">
              <p className="font-mono2 text-3xl lg:text-4xl font-medium text-txt" data-testid={`stat-value-${i}`}>
                <CountUp value={st.v} plus={st.plus} raw={st.raw} />
                {st.suffix || ""}
              </p>
              <p className="mt-2 mono-label !text-[9px] !tracking-[0.2em] !text-txt-3">{st.l}</p>
            </div>
          ))}
        </Reveal>

        {/* one-stop + image bento */}
        <div className="mt-5 grid lg:grid-cols-2 gap-5">
          <Reveal className="card-surface p-8 lg:p-10 h-full">
            <h3 className="font-display uppercase font-bold text-2xl lg:text-3xl text-txt">{s.osTitle}</h3>
            <ul className="mt-6 space-y-4">
              {s.os.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-txt-2" data-testid={`onestop-item-${i}`}>
                  <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-hadron-green" />
                  <span className="text-sm lg:text-base">{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-6 border-t border-line">
              <p className="mono-label !text-[9px]">{s.certsLabel}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {["CE", "CB", "UN38.3", "ISO 9001", "SGS"].map((c) => (
                  <span key={c} className="border border-hadron-cyan/40 bg-hadron-cyan/10 px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.18em] text-hadron-cyan">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="relative overflow-hidden clip-frame border border-line min-h-[320px] lg:min-h-0">
            <video
              poster="/images/facility_poster.jpg"
              autoPlay
              muted
              loop
              playsInline
              data-testid="synergy-facility-video"
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src="/images/facility_loop.mp4" type="video/mp4" />
              <source src="/images/facility_loop.webm" type="video/webm" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
            <div className="absolute bottom-0 left-0 p-6 lg:p-8">
              <p className="mono-label">{t.footer.hub}</p>
              <p className="mt-2 font-display uppercase font-bold text-2xl text-txt">Gdów — Małopolska</p>
            </div>
          </Reveal>
        </div>

        {/* design partner & supply chain */}
        <div className="mt-5 grid md:grid-cols-2 gap-5">
          <Reveal className="card-surface p-8 lg:p-10 relative overflow-hidden" data-testid="partner-band">
            <div className="absolute top-0 left-0 w-full h-px bg-hadron-green" />
            <p className="mono-label !text-[9px] !text-hadron-green">{s.partnerTag}</p>
            <h3 className="mt-4 font-display uppercase font-bold text-2xl lg:text-3xl text-txt">{s.partnerTitle}</h3>
            <p className="mt-4 text-sm lg:text-base text-txt-2 leading-relaxed">{s.partnerText}</p>
          </Reveal>
          <Reveal delay={0.1} className="card-surface p-8 lg:p-10 relative overflow-hidden" data-testid="supply-band">
            <div className="absolute top-0 left-0 w-full h-px bg-hadron-cyan" />
            <p className="mono-label !text-[9px]">{s.supplyTag}</p>
            <h3 className="mt-4 font-display uppercase font-bold text-2xl lg:text-3xl text-txt">{s.supplyTitle}</h3>
            <p className="mt-4 text-sm lg:text-base text-txt-2 leading-relaxed">{s.supplyText}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
